import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced debugging for connection issues
console.log('Supabase Configuration Check:');
console.log('- URL exists:', !!supabaseUrl);
console.log('- URL value:', supabaseUrl);
console.log('- Anon key exists:', !!supabaseAnonKey);
console.log('- Anon key starts with:', supabaseAnonKey?.slice(0,8));


// Enhanced environment variable validation
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Environment variables check:', {
    VITE_SUPABASE_URL: supabaseUrl ? 'Set' : 'Missing',
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'Set' : 'Missing'
  });
  throw new Error('Missing Supabase environment variables. Please check your .env file and ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set. After updating .env, restart your development server.');
}

if (supabaseUrl.includes('your_supabase_url_here') || 
    supabaseAnonKey.includes('your_supabase_anon_key_here')) {
  throw new Error('Please replace placeholder values in your .env file with actual Supabase credentials from your project dashboard (Settings -> API). After updating, restart your development server.');
}

// Initialize Supabase client with improved configuration for reliability
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'blooma_auth'
  },
  global: {
    headers: {
      'apikey': supabaseAnonKey
    }
  }
});

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && error && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  if (typeof error === 'string' && error) return error;
  return null;
}

// Helper to handle common Supabase errors
export function handleSupabaseError(error: unknown): string {
  const message = getErrorMessage(error);
  if (!message) return 'An unexpected error occurred';

  // Network errors — supabase-js often wraps these as plain { message } objects
  if (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('fetch failed') ||
    message.includes('TypeError')
  ) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  // Auth errors
  if (message.includes('Invalid login credentials')) {
    return 'Invalid email or password. Please try again.';
  }
  // Rate limiting
  if (message.includes('Too many requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  // HTTP errors
  if (message.includes('HTTP error!')) {
    return 'Server error. Please try again later.';
  }
  return message;
}

// Helper to check if an error is a "not found" error
export function isNotFoundError(error: unknown): boolean {
  return (error as any)?.code === 'PGRST116';
}

// Helper to retry failed operations with exponential backoff
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000,
  signal?: AbortSignal
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Check if operation has been aborted
      if (signal?.aborted) {
        throw new Error('Operation aborted');
      }
      
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's a not found error (404)
      if (isNotFoundError(error)) {
        throw error;
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries - 1) {
        throw error;
      }
      
      // Exponential backoff with jitter
      const delay = initialDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${Math.round(delay)}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

// Helper to check if user exists
export async function checkUserExists(email: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.admin.listUsers({
      filters: {
        email: email.toLowerCase()
      }
    });

    if (error) throw error;
    return data.users.length > 0;
  } catch (err) {
    console.error('Error checking user existence:', err);
    return false;
  }
}

// Helper to update user metadata
export async function updateUserMetadata(userId: string, metadata: Record<string, any>) {
  try {
    const { error } = await supabase.auth.admin.updateUserById(userId, {
      user_metadata: metadata
    });

    if (error) throw error;
    return true;
  } catch (err) {
    console.error('Error updating user metadata:', err);
    return false;
  }
}

// Helper to check if a gardener profile exists for a user
export async function checkGardenerProfileExists(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('gardener_profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (error && !isNotFoundError(error)) throw error;
    return !!data;
  } catch (err) {
    console.error('Error checking gardener profile:', err);
    return false;
  }
}